import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Eye, FileText, Trash2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { residentRemoveDocumentPresentation } from '@/features/resident/constants/residentDialogProps'
import { ReimbursementDetailFileField } from './ReimbursementDetailFileField'
import { DocumentApprovalControls } from '../modals/DocumentApprovalControls'
import { DocumentReviewMetadata } from '../modals/DocumentReviewMetadata'
import { formatDocumentUploadMetadata } from '../../utils/formatters'
import { UploadedDocumentFileNameHeader } from './UploadedDocumentFileNameHeader'
import { ActionButtonSpinner } from './reimbursementActionButtonLoading'
import type { ResidentDocumentGroup } from '@/features/resident/types'
import type { ResidentUploadedDocumentRow } from '@/features/resident/utils/residentDocuments'
import { attachmentIdsMatch } from '@/features/resident/utils/residentDocuments'
import type {
  ReimbursementDocumentFile,
  ReimbursementDocumentType,
  ReimbursementDocumentReviewStatus,
} from '../../types/reimbursementFormTypes'
import { DOCUMENT_TYPE_LABELS } from '../../types/reimbursementFormTypes'
import {
  getApiUploadFieldForDocType,
  getCurrentDocumentRow,
  getPreviousIncompleteDocumentRow,
  getUploadedRowsForDocType,
  enrichReimbursementDocumentWithPreviousSubmission,
  getReclassifyDisplayFromRow,
  deriveDocumentStatusLabelFromRows,
  getDocumentReviewMetadataFromUploadRow,
  getApprovedAttachmentRows,
  isApprovedAttachmentAction,
  mapUploadRowToReimbursementDocumentFile,
  canRemoveUploadedDocumentAfterReview,
  type ReimbursementImmediateUploadField,
} from '../../utils/reimbursementDocumentBridge'
import type { DocumentReviewIncompleteReason } from '@/features/resident/types/domain'
import type { ReclassifyOption } from '../modals/DocumentApprovalControls'
import { ReimbursementDocumentUploadStatusBadge } from './reimbursementDocumentUploadStatusBadge'
import {
  getReimbursementDocumentUploadStatusAccentTextClass,
  getReimbursementDocumentUploadStatusContainerClasses,
} from '../../utils/reimbursementDocumentUploadStatus'

function uploadRowAttachmentId(
  row: ResidentUploadedDocumentRow | undefined,
): string | number | undefined {
  if (!row) return undefined
  const id = row.reimbursement_attachment_id ?? row.attachment_id ?? row.id
  return id === undefined || id === null ? undefined : id
}

function uploadRowHasViewablePath(row: ResidentUploadedDocumentRow): boolean {
  return Boolean(
    row.filepath || row.file_path || row.attachment_path || row.url || row.path,
  )
}

function UploadedAttachmentCard({
  row,
  docType,
  onDownloadUploaded,
  onRemoveUploaded,
  requestRemoveUploaded,
  allowRemove,
  isRemovingAttachment,
}: {
  row: ResidentUploadedDocumentRow
  docType: ReimbursementDocumentType
  onDownloadUploaded?: ReimbursementRequestDocumentRowProps['onDownloadUploaded']
  onRemoveUploaded?: (attachmentId: string | number) => void
  requestRemoveUploaded: (attachmentId: string | number, fileName: string) => void
  allowRemove: boolean
  isRemovingAttachment: (attachmentId: string | number | null | undefined) => boolean
}) {
  const reviewMetadata = getDocumentReviewMetadataFromUploadRow(row)
  const rowContainerClasses = getReimbursementDocumentUploadStatusContainerClasses(
    reviewMetadata.status ?? 'uploaded',
  )
  const removeId = uploadRowAttachmentId(row)
  const rowPath = String(row.filepath ?? row.file_path ?? '')
  const canViewRow = Boolean(onDownloadUploaded && uploadRowHasViewablePath(row))

  const downloadRow = () =>
    onDownloadUploaded?.({
      attachment_id: row.attachment_id ?? row.id,
      filename: row.filename,
      filepath: rowPath,
    })

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 p-2',
        rowContainerClasses,
      )}
      data-testid={`reimbursement-doc-current-upload-${docType}`}
    >
      <div className="min-w-0 flex-1">
        <UploadedDocumentFileNameHeader
          fileName={String(row.filename ?? 'Uploaded file')}
          onActivate={downloadRow}
          activateDisabled={!canViewRow}
        />
        <DocumentReviewMetadata {...reviewMetadata} />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={downloadRow}
          disabled={!canViewRow}
          className="rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          title="View file"
        >
          <Eye size={15} />
        </button>
        {canRemoveUploadedDocumentAfterReview({
          allowRemove,
          status: reviewMetadata.status,
          action: row.action,
        }) &&
        removeId != null &&
        onRemoveUploaded ? (
          <button
            type="button"
            className="rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={isRemovingAttachment(removeId)}
            aria-busy={isRemovingAttachment(removeId)}
            title={isRemovingAttachment(removeId) ? 'Removing file…' : 'Delete file'}
            onClick={() =>
              requestRemoveUploaded(removeId, String(row.filename ?? 'Uploaded file'))
            }
          >
            {isRemovingAttachment(removeId) ? (
              <ActionButtonSpinner size={15} className="text-red-600" />
            ) : (
              <Trash2 size={15} />
            )}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export interface ReimbursementRequestDocumentRowProps {
  docType: ReimbursementDocumentType
  document: ReimbursementDocumentFile | null
  disabled?: boolean
  uploading?: boolean
  canUpload?: boolean
  canRemove?: boolean
  isLibertyRole?: boolean
  residentDocuments?: ResidentDocumentGroup | null
  statusLabel?: string
  helperText?: string
  headerExtra?: React.ReactNode
  appearance?: 'card' | 'compact'
  tone?: 'default' | 'eviction' | 'skipped'
  labelOverride?: string
  className?: string
  onLocalFileSelect?: (file: File) => void
  onImmediateUpload?: (
    fieldName: ReimbursementImmediateUploadField,
    files: File[],
  ) => void | Promise<void>
  onLocalStatusChange?: (
    status: 'approved' | 'incomplete',
    reason?: string,
    attachmentId?: string | number,
  ) => void
  documentReviewIncompleteReasons?: DocumentReviewIncompleteReason[]
  documentReviewBusy?: boolean
  documentReviewPendingAction?: 'approve' | 'incomplete' | 'reclassify' | null
  /** When set, only the matching attachment row shows review spinners. */
  documentReviewAttachmentId?: string | number | null
  onLocalRemove?: () => void
  onLocalReclassify?: (newDocType: string) => void
  onRemoveUploaded?: (attachmentId: string | number) => void
  /** Attachment id currently being deleted via API (`documentsHook.processingKey`). */
  removingUploadedAttachmentId?: string | number | null
  onDownloadUploaded?: (row: {
    attachment_id?: string | number
    filepath?: string
    filename?: string
  }) => void
  reclassifyOptions?: ReclassifyOption[]
  showSkipAffidavitButton?: boolean
  prefilledSkipAffidavitUrl?: string | null
  canOpenPrefilledSkipAffidavit?: boolean
  /** Return false to block upload (e.g. show eviction lawsuit warning). */
  onBeforeEvictionUpload?: (file: File) => boolean
}

export function ReimbursementRequestDocumentRow({
  docType,
  document,
  disabled,
  uploading,
  canUpload,
  canRemove,
  isLibertyRole,
  residentDocuments,
  statusLabel,
  helperText,
  headerExtra,
  appearance = 'card',
  tone = 'default',
  labelOverride,
  className,
  onLocalFileSelect,
  onImmediateUpload,
  onLocalStatusChange,
  documentReviewIncompleteReasons,
  documentReviewBusy,
  documentReviewPendingAction,
  documentReviewAttachmentId = null,
  onLocalRemove,
  onLocalReclassify,
  onRemoveUploaded,
  removingUploadedAttachmentId = null,
  onDownloadUploaded,
  reclassifyOptions,
  showSkipAffidavitButton,
  prefilledSkipAffidavitUrl,
  canOpenPrefilledSkipAffidavit,
  onBeforeEvictionUpload,
}: ReimbursementRequestDocumentRowProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const [pendingDeleteTarget, setPendingDeleteTarget] = useState<{
    attachmentId?: string | number
    fileName?: string
  } | null>(null)
  /** Parent (resident detail) already shows RemoveDocumentConfirmDialog — skip row-level confirm. */
  const requestRemoveUploaded = useCallback(
    (attachmentId: string | number, fileName: string) => {
      if (onRemoveUploaded) {
        onRemoveUploaded(attachmentId)
        return
      }
      setPendingDeleteTarget({ attachmentId, fileName })
    },
    [onRemoveUploaded],
  )
  const [pendingLargeFile, setPendingLargeFile] = useState<File | null>(null)
  const [previousSubmissionExpanded, setPreviousSubmissionExpanded] = useState(false)
  const label = labelOverride || DOCUMENT_TYPE_LABELS[docType]
  const isCompact = appearance === 'compact'
  const isBlueTone = tone !== 'default'

  const uploadedRows = useMemo(
    () => getUploadedRowsForDocType(residentDocuments, docType),
    [docType, residentDocuments],
  )

  const currentUploadedRow = useMemo(
    () => getCurrentDocumentRow(uploadedRows),
    [uploadedRows],
  )

  const previousIncompleteRow = useMemo(
    () => getPreviousIncompleteDocumentRow(uploadedRows, currentUploadedRow),
    [currentUploadedRow, uploadedRows],
  )

  const approvedUploadedRows = useMemo(
    () => getApprovedAttachmentRows(uploadedRows),
    [uploadedRows],
  )
  const stackApproved = approvedUploadedRows.length > 1
  const currentRowIsApproved = currentUploadedRow
    ? isApprovedAttachmentAction(currentUploadedRow.action)
    : false
  const showCurrentNonApprovedRow = Boolean(
    stackApproved && currentUploadedRow && !currentRowIsApproved,
  )

  const derivedStatus =
    deriveDocumentStatusLabelFromRows(uploadedRows) ||
    statusLabel ||
    (document?.status === 'approved'
      ? 'Approved'
      : document?.status === 'incomplete' || document?.status === 'rejected'
        ? 'Incomplete'
        : document?.status === 'uploaded'
          ? 'Uploaded'
          : '')

  const hasUploaded = Boolean(document || uploadedRows.length > 0)
  const compactStatus = hasUploaded ? derivedStatus || 'Pending' : 'Pending'
  const headerStatusLabel = derivedStatus || 'Pending'
  const chosenFileName = document?.fileName || currentUploadedRow?.filename || ''
  /** Document review (approve/incomplete) disables pickers; never show upload spinners then. */
  const showUploadProgress = Boolean(uploading && !documentReviewBusy)
  const pickerBusy = Boolean(
    disabled || !canUpload || showUploadProgress || documentReviewBusy,
  )
  const allowRemove = Boolean(canRemove && !disabled)
  const canRemoveDocument = (
    status?: ReimbursementDocumentReviewStatus,
    action?: unknown,
  ) => canRemoveUploadedDocumentAfterReview({ allowRemove, status, action })
  const replacementPickerLabel = showUploadProgress ? 'Uploading…' : 'Choose File'
  const isRemovingAttachment = (attachmentId: string | number | null | undefined) =>
    attachmentIdsMatch(attachmentId, removingUploadedAttachmentId)
  const replacementPickerHint = showUploadProgress ? '' : 'No file chosen'

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [])

  const resolvedUploadedAt =
    document?.uploadedAt ??
    (currentUploadedRow?.filedate != null ? currentUploadedRow.filedate : undefined)
  const resolvedUploadedBy = document?.uploadedBy || currentUploadedRow?.uploaded_by

  const uploadMetadata = useMemo(() => {
    if (!resolvedUploadedAt) return ''
    return formatDocumentUploadMetadata(resolvedUploadedAt, resolvedUploadedBy)
  }, [resolvedUploadedAt, resolvedUploadedBy])

  const canViewFile = Boolean(
    (currentUploadedRow &&
      onDownloadUploaded &&
      (currentUploadedRow.filepath ||
        currentUploadedRow.file_path ||
        currentUploadedRow.attachment_path ||
        currentUploadedRow.url ||
        currentUploadedRow.path)) ||
      document?.file,
  )

  const handleViewFile = useCallback(() => {
    if (currentUploadedRow && onDownloadUploaded) {
      onDownloadUploaded({
        attachment_id: currentUploadedRow.attachment_id ?? currentUploadedRow.id,
        filename: currentUploadedRow.filename,
        filepath: String(
          currentUploadedRow.filepath ??
            currentUploadedRow.file_path ??
            currentUploadedRow.attachment_path ??
            currentUploadedRow.url ??
            currentUploadedRow.path ??
            '',
        ),
      })
      return
    }
    if (document?.file) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
      blobUrlRef.current = URL.createObjectURL(document.file)
      window.open(blobUrlRef.current, '_blank', 'noopener,noreferrer')
    }
  }, [document, onDownloadUploaded, currentUploadedRow])

  const handleFileChange = async (file: File | null) => {
    if (!file) return
    if (docType === 'eviction_lawsuit' && onBeforeEvictionUpload && !onBeforeEvictionUpload(file)) {
      return
    }
    const uploadConfig = getApiUploadFieldForDocType(docType)
    if (uploadConfig && onImmediateUpload) {
      await onImmediateUpload(uploadConfig.fieldName, [file])
      return
    }
    onLocalFileSelect?.(file)
  }

  const requestFileSelect = (file: File) => {
    const tenMb = 10 * 1024 * 1024
    if (isCompact && file.size > tenMb) {
      setPendingLargeFile(file)
      return
    }
    void handleFileChange(file)
  }

  const reviewDocument: ReimbursementDocumentFile | null = useMemo(() => {
    const reclassifyFromRow = currentUploadedRow
      ? getReclassifyDisplayFromRow(currentUploadedRow)
      : {}

    const withReclassify = (file: ReimbursementDocumentFile): ReimbursementDocumentFile => ({
      ...file,
      ...reclassifyFromRow,
    })

    if (document) {
      if (!document.previousFileName && previousIncompleteRow) {
        return withReclassify(
          enrichReimbursementDocumentWithPreviousSubmission(document, previousIncompleteRow),
        )
      }
      return withReclassify(document)
    }
    if (!currentUploadedRow) return null
    let file = mapUploadRowToReimbursementDocumentFile(currentUploadedRow)
    file = { ...file, ...reclassifyFromRow }
    if (previousIncompleteRow) {
      file = enrichReimbursementDocumentWithPreviousSubmission(file, previousIncompleteRow)
    }
    return file
  }, [currentUploadedRow, document, previousIncompleteRow])

  const uploadedFileRowStatus =
    reviewDocument?.status ?? document?.status ?? headerStatusLabel
  const uploadedFileRowContainerClasses =
    getReimbursementDocumentUploadStatusContainerClasses(uploadedFileRowStatus)
  const uploadedFileRowAccentClass =
    getReimbursementDocumentUploadStatusAccentTextClass(uploadedFileRowStatus)

  const showLibertyControls = Boolean(
    isLibertyRole && hasUploaded && reviewDocument && !stackApproved,
  )
  const showLibertyControlsForCurrent = Boolean(
    isLibertyRole && hasUploaded && reviewDocument && showCurrentNonApprovedRow,
  )
  /** Skip Affidavit is filed only via the pre-filled portal link — no manual upload/replace. */
  const skipAffidavitPortalOnly = docType === 'skip_affidavit'
  const showReplacementPicker = Boolean(
    canUpload &&
      !disabled &&
      !skipAffidavitPortalOnly &&
      (reviewDocument?.status === 'incomplete' || reviewDocument?.status === 'rejected'),
  )
  const showCompactUploadedStrip = Boolean(
    hasUploaded && !showLibertyControls && document && uploadedRows.length === 0,
  )

  const reviewBusyAttachmentId =
    documentReviewAttachmentId ?? uploadRowAttachmentId(currentUploadedRow)

  const renderDocumentApprovalControls = (
    file: ReimbursementDocumentFile,
    row: ResidentUploadedDocumentRow | undefined,
    options: { isLatest: boolean },
  ) => {
    const attachmentId = row ? uploadRowAttachmentId(row) : file.id
    const rowCanView = row
      ? Boolean(onDownloadUploaded && uploadRowHasViewablePath(row))
      : canViewFile
    const handleView = () => {
      if (row && onDownloadUploaded) {
        onDownloadUploaded({
          attachment_id: row.attachment_id ?? row.id,
          filename: row.filename,
          filepath: String(row.filepath ?? row.file_path ?? ''),
        })
        return
      }
      handleViewFile()
    }
    const isActiveReviewRow = Boolean(
      documentReviewBusy && attachmentIdsMatch(attachmentId, reviewBusyAttachmentId),
    )

    return (
      <DocumentApprovalControls
        fileName={file.fileName}
        fileSize={file.fileSize}
        uploadedAt={
          file.uploadedAt ??
          (resolvedUploadedAt ? new Date(resolvedUploadedAt as string | number | Date) : undefined)
        }
        uploadedBy={file.uploadedBy ?? resolvedUploadedBy}
        status={file.status}
        reviewedAt={file.reviewedAt}
        reviewedBy={file.reviewedBy}
        rejectionReason={file.rejectionReason}
        incompleteReasons={documentReviewIncompleteReasons}
        reclassifiedFrom={file.reclassifiedFrom}
        reclassifiedAt={file.reclassifiedAt}
        reclassifiedBy={file.reclassifiedBy}
        reclassifyOptions={options.isLatest ? reclassifyOptions : undefined}
        onStatusChange={(status, reason) =>
          onLocalStatusChange?.(status, reason, attachmentId ?? undefined)
        }
        onReclassify={options.isLatest ? onLocalReclassify : undefined}
        busy={isActiveReviewRow}
        pendingAction={isActiveReviewRow ? (documentReviewPendingAction ?? null) : null}
        onViewFile={rowCanView ? handleView : undefined}
        onRemoveFile={
          allowRemove &&
          file.status !== 'approved' &&
          file.status !== 'incomplete' &&
          file.status !== 'rejected'
            ? () => {
                if (attachmentId == null) return
                requestRemoveUploaded(attachmentId, file.fileName)
              }
            : undefined
        }
        removeBusy={isRemovingAttachment(attachmentId)}
        rejectLabel="Incomplete"
        disabled={disabled}
      />
    )
  }

  const libertyReviewFileFromRow = (row: ResidentUploadedDocumentRow) => {
    const reclassifyFromRow = getReclassifyDisplayFromRow(row)
    return {
      ...mapUploadRowToReimbursementDocumentFile(row),
      ...reclassifyFromRow,
    }
  }

  const stackedApprovedLibertySection =
    stackApproved && isLibertyRole ? (
      <div className="mt-2 space-y-3">
        {approvedUploadedRows.map((row, index) => {
          const attachmentId = uploadRowAttachmentId(row)
          const isLatest = attachmentIdsMatch(
            attachmentId,
            uploadRowAttachmentId(currentUploadedRow),
          )
          return (
            <div key={String(attachmentId ?? index)}>
              {renderDocumentApprovalControls(libertyReviewFileFromRow(row), row, {
                isLatest,
              })}
            </div>
          )
        })}
      </div>
    ) : null

  const stackedApprovedPmcSection =
    stackApproved && !isLibertyRole ? (
      <div className="mt-2 space-y-2">
        {approvedUploadedRows.map((row, index) => (
          <UploadedAttachmentCard
            key={String(uploadRowAttachmentId(row) ?? index)}
            row={row}
            docType={docType}
            onDownloadUploaded={onDownloadUploaded}
            onRemoveUploaded={onRemoveUploaded}
            requestRemoveUploaded={requestRemoveUploaded}
            allowRemove={allowRemove}
            isRemovingAttachment={isRemovingAttachment}
          />
        ))}
      </div>
    ) : null

  const approvalControls =
    showLibertyControls && reviewDocument
      ? renderDocumentApprovalControls(reviewDocument, currentUploadedRow, { isLatest: true })
      : null

  const libertyCurrentNonApprovedControls =
    showLibertyControlsForCurrent && reviewDocument
      ? renderDocumentApprovalControls(reviewDocument, currentUploadedRow, { isLatest: true })
      : null

  const handleViewPreviousFile = useCallback(() => {
    if (!onDownloadUploaded) return
    if (previousIncompleteRow) {
      onDownloadUploaded({
        attachment_id: previousIncompleteRow.attachment_id ?? previousIncompleteRow.id,
        filename: previousIncompleteRow.filename,
        filepath: String(
          previousIncompleteRow.filepath ??
            previousIncompleteRow.file_path ??
            previousIncompleteRow.attachment_path ??
            previousIncompleteRow.url ??
            previousIncompleteRow.path ??
            reviewDocument?.previousFileUrl ??
            '',
        ),
      })
      return
    }
    if (reviewDocument?.previousFileUrl) {
      onDownloadUploaded({
        filename: reviewDocument.previousFileName,
        filepath: reviewDocument.previousFileUrl,
      })
    }
  }, [onDownloadUploaded, previousIncompleteRow, reviewDocument])

  const previousSubmissionSection =
    reviewDocument?.previousFileName ? (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setPreviousSubmissionExpanded((expanded) => !expanded)}
          className="flex items-center gap-1 text-[10px] text-gray-400 transition-colors hover:text-gray-600"
        >
          {previousSubmissionExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <span>Previous submission (Incomplete)</span>
        </button>
        {previousSubmissionExpanded ? (
          <div className="mt-1.5 border-l-2 border-red-200 pl-3">
            <div className="flex items-start justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText size={12} className="flex-shrink-0 text-red-400" />
                  <span className="truncate text-[11px] font-medium text-red-800">
                    {reviewDocument.previousFileName}
                  </span>
                </div>
                {reviewDocument.previousRejectionReason ? (
                  <p className="text-[10px] text-red-700">
                    <span className="font-medium">Reason:</span> {reviewDocument.previousRejectionReason}
                  </p>
                ) : null}
                {reviewDocument.previousReviewedAt || reviewDocument.previousReviewedBy ? (
                  <p className="text-[10px] italic text-gray-500">
                    Marked incomplete{' '}
                    {reviewDocument.previousReviewedAt
                      ? formatDocumentUploadMetadata(
                          reviewDocument.previousReviewedAt,
                          reviewDocument.previousReviewedBy,
                        )
                      : reviewDocument.previousReviewedBy}
                  </p>
                ) : null}
              </div>
              {onDownloadUploaded &&
              (previousIncompleteRow?.filepath ||
                previousIncompleteRow?.file_path ||
                reviewDocument.previousFileUrl) ? (
                <div className="flex shrink-0 items-center">
                  <button
                    type="button"
                    onClick={handleViewPreviousFile}
                    className="rounded-lg p-1.5 text-red-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
                    title="View file"
                  >
                    <Eye size={15} />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    ) : null

  const compactUploadedStrip =
    showCompactUploadedStrip && document ? (
      <div
        className={cn(
          'mt-2 flex items-center justify-between gap-2 p-2',
          uploadedFileRowContainerClasses,
        )}
      >
        <div className="min-w-0 flex-1">
          <UploadedDocumentFileNameHeader fileName={document.fileName} />
          {reviewDocument ? (
            <DocumentReviewMetadata
              uploadedAt={
                reviewDocument.uploadedAt ??
                (resolvedUploadedAt
                  ? new Date(resolvedUploadedAt as string | number | Date)
                  : undefined)
              }
              uploadedBy={resolvedUploadedBy}
              status={reviewDocument.status}
              reviewedAt={reviewDocument.reviewedAt}
              reviewedBy={reviewDocument.reviewedBy}
              rejectionReason={reviewDocument.rejectionReason}
              reclassifiedFrom={reviewDocument.reclassifiedFrom}
              reclassifiedAt={reviewDocument.reclassifiedAt}
              reclassifiedBy={reviewDocument.reclassifiedBy}
            />
          ) : uploadMetadata ? (
            <p className="text-[10px] italic text-gray-500">Uploaded {uploadMetadata}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleViewFile}
            disabled={!canViewFile}
            className={cn(
              'rounded-lg p-1.5 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40',
              uploadedFileRowAccentClass,
            )}
            title="View file"
          >
            <Eye size={15} />
          </button>
          {canRemoveDocument(
            reviewDocument?.status ?? document.status,
            currentUploadedRow?.action,
          ) ? (
            <button
              type="button"
              onClick={() =>
                setPendingDeleteTarget({
                  attachmentId: document.id,
                  fileName: document.fileName,
                })
              }
              className="rounded-lg p-1.5 text-green-700 transition-colors hover:bg-red-50 hover:text-red-600"
              title="Delete file"
            >
              <Trash2 size={15} />
            </button>
          ) : null}
        </div>
      </div>
    ) : null

  /** PMC: show only the current attachment (LR parity) — prior versions live under Previous submission. */
  const pmcCurrentUploadedCard =
    !approvalControls && !stackApproved && currentUploadedRow ? (
      <div className="mt-2">
        <UploadedAttachmentCard
          row={currentUploadedRow}
          docType={docType}
          onDownloadUploaded={onDownloadUploaded}
          onRemoveUploaded={onRemoveUploaded}
          requestRemoveUploaded={requestRemoveUploaded}
          allowRemove={allowRemove}
          isRemovingAttachment={isRemovingAttachment}
        />
      </div>
    ) : null

  const pmcCurrentNonApprovedCard =
    showCurrentNonApprovedRow && !isLibertyRole && currentUploadedRow ? (
      <div className="mt-2">
        <UploadedAttachmentCard
          row={currentUploadedRow}
          docType={docType}
          onDownloadUploaded={onDownloadUploaded}
          onRemoveUploaded={onRemoveUploaded}
          requestRemoveUploaded={requestRemoveUploaded}
          allowRemove={allowRemove}
          isRemovingAttachment={isRemovingAttachment}
        />
      </div>
    ) : null

  const pmcUploadedSection =
    stackApproved && !isLibertyRole ? (
      <>
        {stackedApprovedPmcSection}
        {pmcCurrentNonApprovedCard}
      </>
    ) : (
      pmcCurrentUploadedCard
    )

  const libertyUploadedSection =
    stackApproved && isLibertyRole ? (
      <>
        {stackedApprovedLibertySection}
        {libertyCurrentNonApprovedControls}
      </>
    ) : (
      approvalControls
    )

  const skipAffidavitPrefilledLabel = 'Submit Pre-filled Skip Reimbursement Affidavit Form'

  const skipAffidavitButton = showSkipAffidavitButton ? (
    canOpenPrefilledSkipAffidavit && prefilledSkipAffidavitUrl && !disabled ? (
      <a
        href={prefilledSkipAffidavitUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        data-testid="reimbursement-skip-claim-affidavit-prefilled-link"
      >
        <Upload size={14} />
        {skipAffidavitPrefilledLabel}
      </a>
    ) : (
      <span
        className="mt-3 inline-flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white opacity-60"
        data-testid="reimbursement-skip-claim-affidavit-prefilled-disabled"
      >
        <Upload size={14} />
        {skipAffidavitPrefilledLabel}
      </span>
    )
  ) : null

  const pendingDeleteFileName =
    pendingDeleteTarget?.fileName ||
    reviewDocument?.fileName ||
    document?.fileName ||
    'this file'

  const deleteFileDialog = (
    <ConfirmDialog
      isOpen={pendingDeleteTarget != null}
      title="Delete file"
      message={`Are you sure you want to delete "${pendingDeleteFileName}"?\n\nThis action cannot be undone.`}
      primaryLabel="Delete"
      secondaryLabel="Cancel"
      variant="error"
      onPrimary={() => {
        setPendingDeleteTarget(null)
        onLocalRemove?.()
      }}
      onSecondary={() => setPendingDeleteTarget(null)}
      onDismiss={() => setPendingDeleteTarget(null)}
      overlayDataTestId="reimbursement-document-delete-confirm"
      primaryButtonDataTestId="reimbursement-document-delete-confirm-yes"
      secondaryButtonDataTestId="reimbursement-document-delete-confirm-cancel"
      {...residentRemoveDocumentPresentation}
    />
  )

  const hiddenFileInput = (
    <input
      ref={inputRef}
      type="file"
      className="hidden"
      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      onChange={(e) => {
        const file = e.target.files?.[0]
        if (file) requestFileSelect(file)
        e.target.value = ''
      }}
    />
  )

  const largeFileDialog = (
    <ConfirmDialog
      isOpen={pendingLargeFile != null}
      title="Large File"
      message={
        pendingLargeFile
          ? label.toLowerCase().includes('lease')
            ? 'WARNING: Your file is larger than 10MB.\n' +
              'We only need the first 2-3 pages of the lease that shows the named parties, start / end dates, and rent amount.\n' +
              'We request you try to compress the file so it takes less storage.\n' +
              'However, if you are not able to compress it further, please continue.\n' +
              'Do you want to continue?'
            : 'WARNING: Your file is larger than 10MB.\n' +
              'We request you try to compress the file so it takes less storage.\n' +
              'However, if you are not able to compress it further, please continue.\n' +
              'Do you want to continue?'
          : ''
      }
      primaryLabel="Yes"
      onPrimary={() => {
        const file = pendingLargeFile
        setPendingLargeFile(null)
        if (file) void handleFileChange(file)
      }}
      secondaryLabel="No"
      onSecondary={() => setPendingLargeFile(null)}
      onDismiss={() => setPendingLargeFile(null)}
      messageLayout="largeFile"
      primaryFirst
      actionStyle="application"
      sectionedChrome
    />
  )

  if (isCompact) {
    return (
      <div className={cn(className)} data-testid={`reimbursement-doc-row-${docType}`}>
        <div className="flex flex-wrap items-center gap-2">
          <p className={cn('text-sm font-semibold', isBlueTone ? 'text-[#173f7a]' : 'text-slate-900')}>
            {label}
          </p>
          <ReimbursementDocumentUploadStatusBadge status={compactStatus} />
        </div>
        {helperText ? (
          <p className={cn('mt-0.5 text-xs', isBlueTone ? 'text-[#315b8a]' : 'text-slate-500')}>
            {helperText}
          </p>
        ) : null}
        {headerExtra}
        {isLibertyRole ? libertyUploadedSection : pmcUploadedSection}
        {compactUploadedStrip}
        {previousSubmissionSection}
        {!libertyUploadedSection &&
        !pmcUploadedSection &&
        !showCompactUploadedStrip &&
        uploadedRows.length === 0 &&
        !showSkipAffidavitButton &&
        !skipAffidavitPortalOnly ? (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-2 py-1.5">
            <button
              type="button"
              disabled={pickerBusy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#eaf4ff] px-3 py-1 text-sm font-medium text-[#1597ff] disabled:cursor-not-allowed disabled:opacity-60"
              aria-busy={showUploadProgress}
            >
              {showUploadProgress ? <ActionButtonSpinner size={12} /> : null}
              {showUploadProgress ? 'Uploading…' : 'Choose File'}
            </button>
            <span
              className={cn(
                'min-w-0 truncate text-sm',
                isBlueTone ? 'text-[#315b8a]' : 'text-slate-500',
              )}
            >
              {showUploadProgress ? '' : chosenFileName || 'No file chosen'}
            </span>
            {document &&
            canRemoveDocument(
              reviewDocument?.status ?? document.status,
              currentUploadedRow?.action,
            ) ? (
              <button
                type="button"
                disabled={isRemovingAttachment(document.id)}
                aria-busy={isRemovingAttachment(document.id)}
                onClick={() => {
                  const attachmentId = document.id
                  if (attachmentId == null) return
                  requestRemoveUploaded(attachmentId, document.fileName)
                }}
                className="ml-auto rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                title={isRemovingAttachment(document.id) ? 'Removing file…' : 'Delete file'}
              >
                {isRemovingAttachment(document.id) ? (
                  <ActionButtonSpinner size={15} className="text-red-600" />
                ) : (
                  <Trash2 size={15} />
                )}
              </button>
            ) : null}
          </div>
        ) : null}
        {showReplacementPicker ? (
          <div className="mt-2 flex w-full flex-col gap-2 rounded-lg border border-dashed border-red-400 bg-red-50 p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-red-800">
              <Upload size={14} className="shrink-0 text-red-700" />
              Upload replacement document
            </div>
            <div className="flex items-center gap-2 rounded-md border border-red-100 bg-white px-2 py-1.5">
              <button
                type="button"
                disabled={pickerBusy}
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded bg-red-100 px-3 py-1 text-sm font-semibold text-red-900 disabled:cursor-not-allowed disabled:opacity-60"
                aria-busy={showUploadProgress}
              >
                {showUploadProgress ? <ActionButtonSpinner size={12} /> : null}
                {replacementPickerLabel}
              </button>
              <span className="min-w-0 truncate text-sm text-gray-800">
                {replacementPickerHint}
              </span>
            </div>
          </div>
        ) : null}
        {skipAffidavitButton}
        {hiddenFileInput}
        {largeFileDialog}
        {deleteFileDialog}
      </div>
    )
  }

  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white p-4 shadow-sm', className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">{label}</p>
          {helperText ? <p className="mt-1 text-xs text-slate-500">{helperText}</p> : null}
        </div>
        <ReimbursementDocumentUploadStatusBadge status={headerStatusLabel} />
      </div>

      {headerExtra}

      {isLibertyRole ? (
        libertyUploadedSection ??
        (!hasUploaded && !skipAffidavitPortalOnly ? (
          <ReimbursementDetailFileField
            label=""
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            disabled={disabled || !canUpload}
            uploading={showUploadProgress}
            status={headerStatusLabel}
            documentStatusBadgeVariant="requestUpload"
            onFileChange={(file) => void handleFileChange(file)}
            uploadedRows={uploadedRows}
            onDownloadUploaded={
              onDownloadUploaded
                ? (row) =>
                    onDownloadUploaded({
                      attachment_id: row.attachment_id ?? row.id,
                      filename: row.filename,
                      filepath: String(row.filepath ?? row.file_path ?? ''),
                    })
                : undefined
            }
            canRemoveUploaded={allowRemove}
            removingUploadedAttachmentId={removingUploadedAttachmentId}
            onRequestRemoveUploaded={
              onRemoveUploaded ? (id) => onRemoveUploaded(id) : undefined
            }
          />
        ) : null)
      ) : hasUploaded && (document || currentUploadedRow) ? (
          pmcUploadedSection ?? (
            <div
              className={cn(
                'flex items-center justify-between p-2',
                uploadedFileRowContainerClasses,
              )}
            >
              <div className="min-w-0 flex-1">
                <UploadedDocumentFileNameHeader fileName={document?.fileName ?? chosenFileName} />
                {reviewDocument ? (
                  <DocumentReviewMetadata
                    uploadedAt={reviewDocument.uploadedAt ?? document?.uploadedAt}
                    uploadedBy={reviewDocument.uploadedBy ?? document?.uploadedBy}
                    status={reviewDocument.status ?? document?.status}
                    reviewedAt={reviewDocument.reviewedAt}
                    reviewedBy={reviewDocument.reviewedBy}
                    rejectionReason={reviewDocument.rejectionReason}
                    reclassifiedFrom={reviewDocument.reclassifiedFrom}
                    reclassifiedAt={reviewDocument.reclassifiedAt}
                    reclassifiedBy={reviewDocument.reclassifiedBy}
                  />
                ) : document?.uploadedAt ? (
                  <p className="text-[10px] italic text-gray-500">
                    Uploaded {formatDocumentUploadMetadata(document.uploadedAt, document.uploadedBy)}
                  </p>
                ) : uploadMetadata ? (
                  <p className="text-[10px] italic text-gray-500">Uploaded {uploadMetadata}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={handleViewFile}
                  disabled={!canViewFile}
                  className={cn(
                    'rounded-lg p-1.5 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40',
                    uploadedFileRowAccentClass,
                  )}
                  title="View file"
                >
                  <Eye size={15} />
                </button>
                {document &&
                canRemoveDocument(
                  reviewDocument?.status ?? document.status,
                  currentUploadedRow?.action,
                ) ? (
                  <button
                    type="button"
                    disabled={isRemovingAttachment(document.id)}
                    aria-busy={isRemovingAttachment(document.id)}
                    onClick={() => {
                      const attachmentId = document.id
                      if (attachmentId == null) return
                      requestRemoveUploaded(attachmentId, document.fileName)
                    }}
                    className="rounded-lg p-1.5 text-green-700 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                    title={isRemovingAttachment(document.id) ? 'Removing file…' : 'Delete file'}
                  >
                    {isRemovingAttachment(document.id) ? (
                      <ActionButtonSpinner size={15} className="text-red-600" />
                    ) : (
                      <Trash2 size={15} />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          )
        ) : (
          <ReimbursementDetailFileField
            label=""
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            disabled={disabled || !canUpload}
            uploading={showUploadProgress}
            status={headerStatusLabel}
            documentStatusBadgeVariant="requestUpload"
            onFileChange={(file) => void handleFileChange(file)}
            uploadedRows={uploadedRows}
            onDownloadUploaded={
              onDownloadUploaded
                ? (row) =>
                    onDownloadUploaded({
                      attachment_id: row.attachment_id ?? row.id,
                      filename: row.filename,
                      filepath: String(row.filepath ?? row.file_path ?? ''),
                    })
                : undefined
            }
            canRemoveUploaded={allowRemove}
            removingUploadedAttachmentId={removingUploadedAttachmentId}
            onRequestRemoveUploaded={
              onRemoveUploaded
                ? (id) => onRemoveUploaded(id)
                : undefined
            }
          />
        )}

      {!isLibertyRole ? previousSubmissionSection : null}
      {!libertyUploadedSection && !isLibertyRole && showReplacementPicker ? (
        <div className="mt-3 flex w-full flex-col gap-2 rounded-lg border border-dashed border-red-400 bg-red-50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-red-800">
            <Upload size={14} className="shrink-0 text-red-700" />
            Upload replacement document
          </div>
          <div className="flex items-center gap-2 rounded-md border border-red-100 bg-white px-2 py-1.5">
            <button
              type="button"
              disabled={pickerBusy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded bg-red-100 px-3 py-1 text-sm font-semibold text-red-900 disabled:cursor-not-allowed disabled:opacity-60"
              aria-busy={showUploadProgress}
            >
              {showUploadProgress ? <ActionButtonSpinner size={12} /> : null}
              {replacementPickerLabel}
            </button>
            <span className="min-w-0 truncate text-sm text-gray-800">{replacementPickerHint}</span>
          </div>
        </div>
      ) : null}

      {skipAffidavitButton}
      {hiddenFileInput}
      {largeFileDialog}
      {deleteFileDialog}
    </div>
  )
}

export function YesNoRadioGroup({
  name,
  value,
  onChange,
  disabled,
  readOnly,
  label,
  tone = 'default',
}: {
  name: string
  value: boolean | null
  onChange: (value: boolean) => void
  disabled?: boolean
  readOnly?: boolean
  label: string
  tone?: 'default' | 'eviction' | 'skipped'
}) {
  const isBlueTone = tone !== 'default'
  const locked = Boolean(disabled || readOnly)
  return (
    <fieldset
      className={cn(
        'space-y-2',
        readOnly && 'pointer-events-none',
        disabled && !readOnly && 'pointer-events-none opacity-70',
      )}
    >
      <legend className={cn('text-sm font-semibold', isBlueTone ? 'text-[#173f7a]' : 'text-slate-900')}>
        {label}
      </legend>
      <div className="flex flex-wrap gap-4">
        {[
          { val: false, text: 'No' },
          { val: true, text: 'Yes' },
        ].map(({ val, text }) => (
          <label
            key={text}
            className={cn(
              'inline-flex items-center gap-2 text-sm',
              isBlueTone ? 'text-[#173f7a]' : 'text-slate-700',
            )}
          >
            <input
              type="radio"
              name={name}
              checked={value === val}
              onChange={() => onChange(val)}
              disabled={locked}
              className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            {text}
          </label>
        ))}
      </div>
    </fieldset>
  )
}
